import React, { useState, useEffect, useRef } from "react";
import { getFirestore, collection, onSnapshot } from "firebase/firestore";
import { ChevronDown } from "lucide-react";

const db = getFirestore();

const ClassesDropdown = ({ selectedIds = [], onChange, isSecAdmin, theme = 'light' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [localSelected, setLocalSelected] = useState(selectedIds);
  const [menuStyle, setMenuStyle] = useState({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    setLocalSelected(selectedIds);
  }, [selectedIds]);

  // 🔹 Fetch classes in real-time
  // useEffect(() => {
  //   const unsubscribe = onSnapshot(collection(db, "classSchedule"), (snapshot) => {
  //     const items = snapshot.docs.map((doc) => ({
  //       id: doc.id,
  //       ...doc.data(),
  //     }));
  //     setClasses(items);
  //   });
  //   return () => unsubscribe();
  // }, []);

  useEffect(() => {
  const unsubscribe = onSnapshot(collection(db, "classSchedule"), (snapshot) => {
    const newItems = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    setClasses((prev) => {
      if (JSON.stringify(prev.map((i) => i.id)) !== JSON.stringify(newItems.map((i) => i.id))) {
        return newItems;
      }
      return prev;
    });
  });
  return unsubscribe;
}, []);


  // 🔹 Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    const timeout = setTimeout(() => setLocalSelected(selectedIds), 50);
    return () => clearTimeout(timeout);
  }, [selectedIds]);

  useEffect(() => {
    if (!isOpen) return;

    const updateMenuPosition = () => {
      if (!dropdownRef.current) return;
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const desiredMaxHeight = 256;
      const placeAbove = spaceBelow < 220 && spaceAbove > spaceBelow;

      let maxHeight = desiredMaxHeight;
      let top = rect.bottom + window.scrollY;

      if (placeAbove) {
        maxHeight = Math.min(desiredMaxHeight, Math.max(spaceAbove - 8, 120));
        top = rect.top + window.scrollY - maxHeight;
      } else {
        maxHeight = Math.min(desiredMaxHeight, Math.max(spaceBelow - 8, 120));
      }

      top = Math.max(8, top);

      setMenuStyle({
        top: `${top}px`,
        left: `${rect.left + window.scrollX}px`,
        width: `${rect.width}px`,
        maxHeight: `${maxHeight}px`,
      });
    };

    updateMenuPosition();
    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);
    return () => {
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
    };
  }, [isOpen]);

  // 🔹 Toggle single selection
  const handleToggle = (id) => {
    let updated = [...localSelected];
    if (updated.includes(id)) {
      updated = updated.filter((x) => x !== id);
    } else {
      updated.push(id);
    }
    setLocalSelected(updated);
    onChange(updated);
  };

  const handleSelectAll = () => {
    setLocalSelected(() => {
      const allIds = classes.map((c) => c.id);
      onChange(allIds);
      return allIds;
    });
  };

  const handleClearAll = () => {
    setLocalSelected(() => {
      onChange([]);
      return [];
    });
  };

  return (
    <div className="w-full relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => !isSecAdmin && setIsOpen((prev) => !prev)}
        disabled={isSecAdmin}
        className={`w-full flex justify-between items-center border rounded-lg px-3 py-2 text-left focus:outline-none ${
          theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-white hover:border-blue-400'
            : 'bg-white border-gray-300 text-gray-800 hover:border-blue-400'
        }`}
      >
        <span className="truncate">
          {localSelected.length === 0
            ? "Select Classes"
            : classes
              .filter((c) => localSelected.includes(c.id))
              .map((c) => c.className)
              .slice(0, 1)
              .join(", ") + (localSelected.length > 1 ? "..." : "")}
        </span>

        <ChevronDown
          className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""
            }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className={`fixed z-[9999] border rounded-lg shadow-lg overflow-y-auto animate-fadeIn min-w-[300px] ${
            theme === 'dark'
              ? 'bg-gray-700 border-gray-600'
              : 'bg-white border-gray-300'
          }`}
          style={menuStyle}
        >
          {/* Controls */}
          <div className={`w-full flex justify-between items-center px-3 py-2 border-b text-sm font-medium ${
            theme === 'dark'
              ? 'bg-gray-600 border-gray-500'
              : 'bg-gray-50 border-gray-200'
          }`}>
            <button onClick={handleSelectAll} className="text-blue-600 hover:underline">
              Select All
            </button>
            <button onClick={handleClearAll} className="text-red-500 hover:underline">
              Clear All
            </button>
          </div>

          {/* List of Classes */}
          <ul className="py-1 text-sm">
            {classes.map((cls) => {
              const checked = localSelected.includes(cls.id) || cls.allAttend === true;
              return (
                <li
                  key={cls.id}
                  className={`flex items-center justify-between px-3 py-2 ${
                    theme === 'dark'
                      ? 'hover:bg-gray-600'
                      : 'hover:bg-blue-50'
                  }`}
                >

                  <div className="flex items-center space-x-2">
                    {/* <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggle(cls.id)}
                      className="h-4 w-4 accent-blue-600"
                    /> */}
                    <input
                      type="checkbox"
                      checked={localSelected.includes(cls.id)}
                      onChange={() => {
                        setLocalSelected((prev) => {
                          const updated = prev.includes(cls.id)
                            ? prev.filter((x) => x !== cls.id)
                            : [...prev, cls.id];
                          onChange(updated);
                          return updated;
                        });
                      }}
                      className="h-4 w-4 accent-blue-600 cursor-pointer"
                    />

                    <span
                      className={`${
                        cls.allAttend 
                          ? 'font-semibold text-blue-600' 
                          : theme === 'dark' 
                            ? 'text-gray-200' 
                            : 'text-gray-800'
                      }`}
                    >
                      {cls.className}
                    </span>
                  </div>

                  {cls.allAttend && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      All Attend
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ClassesDropdown;
